/**
 * Look up a candidate or a client by name, from anywhere on a rail page.
 *
 * **Built on the search that already exists rather than on a new index.**
 * `/candidates` and `/clients` both take a `search` parameter and both scope
 * themselves to the caller, so this asks each of them and groups the answers.
 * Nothing here decides who may see what.
 *
 * Two entities, not eight. Those are the two things somebody looks up by name:
 * a person they are ringing and a company they are placing into. Calls,
 * submissions and placements are found *through* those two, and a results list
 * that offers five kinds of row is one nobody reads to the bottom of.
 *
 * This screen previously had no search at all, on the stated grounds that a
 * box which does nothing is worse than none. That reasoning is why this one
 * queries real endpoints and links to real pages rather than being a styled
 * input.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Search, X } from 'lucide-react';
import { client } from '../api/client';
import { formatPhone } from '../lib/phone';
import { titleCaseName } from '../lib/titleCase';
import { isStaff, useAuth } from '../store/auth';
import type { Candidate, Client, Page } from '../types';

/**
 * How long to wait after the last keystroke.
 *
 * 300ms rather than firing per character. The candidate table holds 319,404
 * rows and the search is an ILIKE across it; a request per keystroke would put
 * eight of them in flight for one word.
 */
const DEBOUNCE_MS = 300;

/** Below this, the answer would be most of the book. */
const MIN_QUERY = 2;

export default function GlobalSearch() {
  const user = useAuth((s) => s.user);
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(term.trim()), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [term]);

  // Clicking away closes the results without clearing what was typed, so
  // reopening does not mean retyping.
  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      if (box.current && !box.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const enabled = debounced.length >= MIN_QUERY && isStaff(user);

  const candidates = useQuery<Page<Candidate>>({
    queryKey: ['search', 'candidates', debounced],
    enabled,
    queryFn: async () =>
      (await client.get('/api/v1/candidates', {
        params: { search: debounced, limit: 5 },
      })).data,
  });

  const clients = useQuery<Page<Client>>({
    queryKey: ['search', 'clients', debounced],
    enabled,
    queryFn: async () =>
      (await client.get('/api/v1/clients', {
        params: { search: debounced, limit: 5 },
      })).data,
  });

  // Employees have no recruitment work, so both endpoints refuse them. An
  // input that can only produce a 403 is the dead control this exists to avoid.
  if (!isStaff(user)) return null;

  const loading = enabled && (candidates.isFetching || clients.isFetching);
  const candidateRows = candidates.data?.items ?? [];
  const clientRows = clients.data?.items ?? [];
  const nothing =
    enabled && !loading && candidateRows.length === 0 && clientRows.length === 0;

  const go = (to: string) => {
    setOpen(false);
    setTerm('');
    navigate(to);
  };

  return (
    <div ref={box} className="relative w-full max-w-xs">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
      <input
        type="search"
        className="input py-1.5 pl-8 pr-8 text-sm"
        placeholder="Search candidates and clients"
        value={term}
        onChange={(event) => {
          setTerm(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setOpen(false);
        }}
        aria-label="Search candidates and clients"
      />
      {term && (
        <button
          type="button"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-700"
          onClick={() => {
            setTerm('');
            setOpen(false);
          }}
          aria-label="Clear search"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}

      {open && term.trim().length > 0 && (
        <div className="absolute left-0 right-0 top-full z-40 mt-1 max-h-96 overflow-y-auto rounded-xl bg-white p-1 shadow-lg ring-1 ring-gray-200">
          {debounced.length < MIN_QUERY ? (
            <p className="px-3 py-3 text-xs text-gray-500">
              Keep typing — at least {MIN_QUERY} characters.
            </p>
          ) : loading ? (
            <p className="flex items-center gap-2 px-3 py-3 text-xs text-gray-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Searching…
            </p>
          ) : nothing ? (
            <p className="px-3 py-3 text-xs text-gray-500">
              Nothing matching “{debounced}”.
            </p>
          ) : (
            <>
              {candidateRows.length > 0 && (
                <>
                  <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    Candidates
                  </p>
                  {candidateRows.map((row) => (
                    <button
                      key={row.id}
                      type="button"
                      className="flex w-full items-baseline justify-between gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-gray-50"
                      /* The list, narrowed to this name -- there is no
                         `/candidates/:id` route, and linking to one would
                         bounce off the catch-all to the home redirect.
                         CandidatesPage seeds its search box from this. */
                      onClick={() =>
                        go(
                          `/candidates?search=${encodeURIComponent(
                            row.phone || row.fullName,
                          )}`,
                        )
                      }
                    >
                      <span className="min-w-0 truncate text-sm text-gray-900">
                        {titleCaseName(row.fullName)}
                      </span>
                      <span className="shrink-0 text-xs tabular-nums text-gray-400">
                        {formatPhone(row.phone)}
                      </span>
                    </button>
                  ))}
                </>
              )}

              {clientRows.length > 0 && (
                <>
                  <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    Clients
                  </p>
                  {clientRows.map((row) => (
                    <button
                      key={row.id}
                      type="button"
                      className="flex w-full items-baseline justify-between gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-gray-50"
                      onClick={() => go(`/clients/${row.id}`)}
                    >
                      <span className="min-w-0 truncate text-sm text-gray-900">
                        {row.name}
                      </span>
                      <span className="shrink-0 text-xs text-gray-400">
                        {[row.city, row.state].filter(Boolean).join(', ')}
                      </span>
                    </button>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
